import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Moon, Heart, Star, Users, Headphones, Sparkles, ArrowRight, Shield, Copy } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const LandingHero = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();

  if (isAuthenticated) return null;

  return (
    <div data-testid="landing-hero">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-8 pb-16 md:pt-16 md:pb-24">
        <div className="absolute top-10 left-10 w-64 h-64 bg-old-rose/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-80 h-80 bg-gold/10 rounded-full blur-3xl" />

        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/10 rounded-full mb-6 border border-gold/20">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">{t('hero.badge')}</span>
              </div>

              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-foreground leading-[1.1] mb-6">
                {t('hero.title')}
              </h1>

              <p className="text-lg md:text-xl text-muted-foreground font-body leading-relaxed mb-8 max-w-lg">
                {t('hero.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="hero-cta-primary"
                  onClick={() => navigate('/login')}
                  className="rounded-full bg-old-rose hover:bg-old-rose/80 text-night-blue font-bold px-8 py-6 text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
                >
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  onClick={() => document.getElementById('bookshelf-section')?.scrollIntoView({ behavior: 'smooth' })}
                  className="rounded-full border-2 border-night-blue/20 hover:border-night-blue/40 px-8 py-6 text-lg"
                >
                  {t('hero.cta2')}
                </Button>
              </div>

              <div className="flex items-center gap-6 mt-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Shield className="w-4 h-4 text-green-600" />
                  <span>{t('hero.safe')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Heart className="w-4 h-4 text-old-rose" />
                  <span>{t('hero.love')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Star className="w-4 h-4 text-gold" />
                  <span>{t('hero.trial')}</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative hidden md:block"
            >
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-soft-white">
                  <img src="https://images.unsplash.com/photo-1612594393915-89f8dd445f03?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" alt="" className="w-full h-[420px] object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-night-blue/30 via-transparent to-transparent" />
                </div>
                <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }} className="absolute -left-6 bottom-12 bg-card/95 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <p className="font-bold text-foreground">{t('hero.stories_count')}</p>
                      <p className="text-xs text-muted-foreground">{t('hero.stories_growing')}</p>
                    </div>
                  </div>
                </motion.div>
                <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }} className="absolute -right-4 top-8 bg-card/95 backdrop-blur-md rounded-2xl p-3 shadow-xl border border-border">
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-4 h-4 text-gold fill-gold" />)}
                    </div>
                    <span className="text-sm font-bold text-foreground">{t('hero.loved')}</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 border-y border-border/50 bg-card/30">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-3 gap-6 text-center">
            {[
              { icon: BookOpen, label: t('stats.stories'), value: '50+', color: 'text-gold' },
              { icon: Users, label: t('stats.storytellers'), value: '10+', color: 'text-old-rose' },
              { icon: Heart, label: t('stats.reads'), value: '500+', color: 'text-red-400' },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex flex-col items-center">
                <stat.icon className={`w-6 h-6 ${stat.color} mb-2`} />
                <p className="font-display text-2xl md:text-3xl text-foreground">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl text-foreground mb-3">{t('how.title')}</h2>
            <p className="text-muted-foreground text-lg max-w-md mx-auto">{t('how.subtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              { icon: BookOpen, step: '1', title: t('how.step1.title'), description: t('how.step1.desc'), color: 'bg-gold/15 text-gold' },
              { icon: Headphones, step: '2', title: t('how.step2.title'), description: t('how.step2.desc'), color: 'bg-old-rose/20 text-old-rose' },
              { icon: Moon, step: '3', title: t('how.step3.title'), description: t('how.step3.desc'), color: 'bg-night-blue/10 text-night-blue' },
            ].map((item, i) => (
              <motion.div key={item.step} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="text-center group">
                <div className="relative inline-flex mb-6">
                  <div className={`w-20 h-20 rounded-2xl ${item.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <item.icon className="w-9 h-9" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-foreground text-background text-sm font-bold flex items-center justify-center">{item.step}</div>
                </div>
                <h3 className="font-display text-2xl text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-card/30 border-y border-border/50">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="font-display text-4xl md:text-5xl text-foreground mb-3">{t('testimonials.title')}</h2>
            <p className="text-muted-foreground text-lg">{t('testimonials.subtitle')}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: t('testimonial.1.quote'), name: t('testimonial.1.name'), role: t('testimonial.1.role') },
              { quote: t('testimonial.2.quote'), name: t('testimonial.2.name'), role: t('testimonial.2.role') },
              { quote: t('testimonial.3.quote'), name: t('testimonial.3.name'), role: t('testimonial.3.role') },
            ].map((testimonial, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-card rounded-2xl p-6 shadow-sm border border-border/50 hover:shadow-md transition-shadow">
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-4 h-4 text-gold fill-gold" />)}
                </div>
                <p className="text-foreground leading-relaxed mb-4 font-body italic">{testimonial.quote}</p>
                <div>
                  <p className="font-bold text-foreground text-sm">{testimonial.name}</p>
                  <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Share Banner */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-card rounded-3xl p-6 md:p-8 border border-border shadow-sm flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-display text-2xl text-foreground mb-2">{t('hero.share_title')}</h3>
              <p className="text-muted-foreground text-sm">{t('hero.share_desc')}</p>
            </div>
            <div className="flex gap-3">
              <button data-testid="hero-share-whatsapp" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(t('hero.share_text') + " https://omasverhaaltjes.nl")}`, '_blank')} className="w-12 h-12 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </button>
              <button data-testid="hero-share-facebook" onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://omasverhaaltjes.nl")}`, '_blank')} className="w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center transition-colors">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </button>
              <button data-testid="hero-share-copy" onClick={() => navigator.clipboard?.writeText("https://omasverhaaltjes.nl")} className="w-12 h-12 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors">
                <Copy className="w-5 h-5 text-foreground" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
            <div className="bg-gradient-to-br from-old-rose/20 via-gold/10 to-old-rose/20 rounded-3xl p-8 md:p-12 border border-old-rose/30">
              <Moon className="w-12 h-12 text-gold mx-auto mb-4" />
              <h2 className="font-display text-3xl md:text-4xl text-foreground mb-4">{t('cta.title')}</h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">{t('cta.subtitle')}</p>
              <Button
                data-testid="cta-bottom-signup"
                onClick={() => navigate('/login')}
                className="rounded-full bg-night-blue hover:bg-night-blue/90 text-cream font-bold px-10 py-6 text-lg shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all"
              >
                {t('cta.button')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-sm text-muted-foreground mt-4">{t('cta.note')}</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LandingHero;
